import type { DraftSabotageEffect } from '@/hooks/useDraftSabotageEffects';
import { describeEffectExpiry } from '@/lib/nflSabotageBanners';

/**
 * SAB-931: Guess Who masking — the highest-stakes item in the V2 room.
 *
 * ── WHY THIS ONE IS DIFFERENT ───────────────────────────────────────────────
 *
 * `hide_info` (Roughing the Drafter) and `league_guess_who` are ENTIRELY
 * client-rendered. Unlike timer_half or force_auto, there is no server-side effect to fall
 * back on: the masking IS the card. So in the V2 room, which had none of this, a player
 * bought a card, the server recorded it, and the target saw straight through it. Nothing
 * failed -- no error, no log, no failing test. It looked exactly like the card working.
 *
 * That is why the logic lives here, pure, rather than as conditions inside JSX: every
 * combination has to be assertable, in BOTH directions. A mask that hides from everyone is
 * as broken as one that hides from nobody, and both look like success from one side.
 *
 * ── THE ATTACKER IS MASKED TOO (SAB-450 item 2) ─────────────────────────────
 *
 * `league_guess_who` applies to EVERYONE, including whoever played it. An earlier version
 * exempted the attacker, which meant the person who paid saw the banner while their own
 * board stayed readable -- the reported "signature mechanic is a no-op". The spec is
 * unambiguous ("Everyone is in Guess Who mode this round"), and an attacker keeping perfect
 * information while the league goes blind would be strictly dominant. `hide_info` stays
 * per-target; only the league-wide card is universal.
 *
 * ── WHERE THE MASK IS APPLIED, AND WHY IT IS ONE PLACE ──────────────────────
 *
 * `maskPoolRow` runs at the MAPPING step, before `filterDraftPool` ever sees a row. So a
 * masked player's real name is not present as matchable text and search cannot return them
 * -- with no second "is this hidden" rule for search to keep in sync with the renderer.
 * Search asking "does this row match" and the renderer asking "what does this row say" then
 * read the same object, which is the only arrangement where they cannot disagree.
 *
 * Mask, then filter. The reverse order would search real names and render fake ones.
 */

/** What a masked player's name reads as. Exported so tests assert the literal, not a copy. */
export const MASKED_NAME = '???';

/** What a masked player's position reads as. The spec hides position; the column stays. */
export const MASKED_POSITION = '?';

export interface GuessWhoState {
  /** Is the viewer under a mask right now? */
  active: boolean;
  /**
   * Picks at or after this number are masked in the log and on the board. Null when not
   * masked. Picks made BEFORE the card was played stay visible -- you cannot unsee what was
   * already public, and the strategic point is hiding what comes next (SAB-450 item 5).
   */
  maskedSincePick: number | null;
  /** Countdown for the soonest-ending mask, or '' when not masked. */
  expiryText: string;
}

export interface ResolveGuessWhoArgs {
  activeEffects: DraftSabotageEffect[] | null | undefined;
  myTeamId: string | null | undefined;
  currentPickNumber: number;
  picksPerRound: number;
}

/** The effects that mask THIS viewer. League-wide masks everyone; hide_info only its target. */
function masksForViewer(
  effects: DraftSabotageEffect[], myTeamId: string | null | undefined,
): DraftSabotageEffect[] {
  return effects.filter(e =>
    e.active && (
      e.effect_type === 'league_guess_who'
      // `myTeamId != null` matters: without it a hide_info row whose target_team_id is NULL
      // would mask a viewer who has no team, via null === null. Same trap that let a
      // sabotage banner reach a spectator.
      || (e.effect_type === 'hide_info' && myTeamId != null && e.target_team_id === myTeamId)
    ),
  );
}

export function resolveGuessWho(args: ResolveGuessWhoArgs): GuessWhoState {
  const relevant = masksForViewer(args.activeEffects ?? [], args.myTeamId);
  if (relevant.length === 0) return { active: false, maskedSincePick: null, expiryText: '' };

  // The EARLIEST play wins: overlapping masks mask the union of what each hides, so the
  // boundary is the first one, not the latest.
  const earliest = relevant.reduce(
    (min, e) => Math.min(min, e.pick_number ?? Number.POSITIVE_INFINITY),
    Number.POSITIVE_INFINITY,
  );

  // The SOONEST-ending mask supplies the countdown. One line can only show one number, and
  // promising the longer remaining time would tell the viewer they are blind for longer
  // than they are.
  const soonest = relevant
    .map(e => describeEffectExpiry(e, args.currentPickNumber, args.picksPerRound))
    .filter(Boolean)
    .sort()[0] ?? '';

  return {
    active: true,
    maskedSincePick: Number.isFinite(earliest) ? earliest : null,
    expiryText: soonest,
  };
}

/** Is this pick hidden from the viewer? Picks before the mask began stay visible. */
export function isPickMasked(state: GuessWhoState, pickNumber: number): boolean {
  return state.active && state.maskedSincePick != null && pickNumber >= state.maskedSincePick;
}

/**
 * The shape the pool renders. Only the fields a mask has to strip are named; anything else
 * on the row passes through, so this cannot silently drop a field a caller depends on.
 */
export interface MaskablePoolRow {
  full_name: string;
  position: string;
  nfl_team?: string | null;
  injury_status?: string | null;
  proj_sab_pts?: number | null;
  adp?: number | null;
  bye_week?: number | null;
  /** SAB-813: roster state (ACT / IR / PUP ...). Stripped -- see maskPoolRow. */
  status?: string | null;
}

/**
 * Strip everything except the face.
 *
 * ── THE CARD IS A RECOGNITION GAME, NOT A BLACKOUT ──────────────────────────
 *
 * From the sabotage store, which is the spec:
 *
 *   Guess Who (10 SP)            "Target opponent sees ONLY player headshots for their
 *                                 next pick. No name, stats, position, team, ADP."
 *   Roughing the Drafter (50 SP) "ALL opponents draft in Guess Who mode for one full round."
 *
 * The headshot is the ONE signal that survives -- it is the entire card. A masked viewer is
 * meant to recognise a face and back their own football knowledge, which is why it is worth
 * 10 SP and why it is called Guess Who.
 *
 * POSITION IS STRIPPED. An earlier version of this file kept it, with a plausible-sounding
 * justification ("you know a RB is available, not which one"). That was wrong against the
 * spec, which names position explicitly, and wrong in effect: with position visible a
 * masked viewer can filter the pool to one slot and draft competently without ever
 * identifying anyone. The card would be an annoyance instead of a blind.
 *
 * Projections, ADP and bye week go for the related reason: each is close enough to a
 * fingerprint that one of them plus a rankings sheet re-identifies the player, defeating
 * the card without a name ever appearing.
 *
 * `headshot_url` is deliberately NOT in the list. If it is ever stripped, the card shows a
 * grid of identical grey circles and communicates nothing at all -- the failure mode is a
 * card that is bought, works, and is indistinguishable from being broken.
 */
export function maskPoolRow<T extends MaskablePoolRow>(row: T): T {
  return {
    ...row,
    full_name: MASKED_NAME,
    position: MASKED_POSITION,
    nfl_team: null,
    // SAB-813: BOTH availability columns, not just one. The pool row used to carry the
    // roster state in the `injury_status` field, so nulling that one field happened to
    // cover it. Once they became two real fields, masking only `injury_status` would have
    // leaked "IR" / "PUP" straight through a Guess Who card -- the V2 twin of the legacy
    // leak in SAB-935, created by a rename rather than by new code.
    injury_status: null,
    status: null,
    proj_sab_pts: null,
    adp: null,
    bye_week: null,
  };
}

/** Mask a whole pool when active; return it untouched when not. */
export function maskPool<T extends MaskablePoolRow>(rows: T[], state: GuessWhoState): T[] {
  return state.active ? rows.map(maskPoolRow) : rows;
}
